/**
 * General Utility Functions
 *
 * Core utilities used across the application.
 */

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utility function to merge class names with clsx + tailwind-merge
 * Ensures conflicting Tailwind classes are properly resolved (last wins)
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Convert kebab-case string to proper Title Case for display
 * Handles special cases like API, UUID, etc.
 */
export function formatPathSegment(segment: string): string {
  if (!segment) return "";

  // Special cases mapping for common terms
  const specialCases: Record<string, string> = {
    "api-docs": "API Documentation",
    api: "API",
    docs: "Documentation",
    uuid: "UUID",
    ui: "UI",
    ux: "UX",
    json: "JSON",
    xml: "XML",
    html: "HTML",
    css: "CSS",
    js: "JavaScript",
    ts: "TypeScript",
    sql: "SQL",
    db: "Database",
    admin: "Admin",
    auth: "Authentication",
    oauth: "OAuth",
    jwt: "JWT",
    sso: "SSO",
    cms: "CMS",
    crm: "CRM",
    erp: "ERP",
    hr: "HR",
    it: "IT",
    qa: "QA",
    qr: "QR",
    pdf: "PDF",
    csv: "CSV",
    svg: "SVG",
    png: "PNG",
    jpg: "JPG",
    jpeg: "JPEG",
    gif: "GIF",
    mp4: "MP4",
    mp3: "MP3",
    zip: "ZIP",
    tar: "TAR",
    gz: "GZ",
    http: "HTTP",
    https: "HTTPS",
    ftp: "FTP",
    ssh: "SSH",
    tcp: "TCP",
    udp: "UDP",
    dns: "DNS",
    ip: "IP",
    url: "URL",
    uri: "URI",
    rss: "RSS",
    seo: "SEO",
    vpc: "VPC",
    aws: "AWS",
    gcp: "GCP",
    cdn: "CDN",
    ssl: "SSL",
    tls: "TLS",
    locations: "Facilities",
    locationaggregate: "Facility Aggregates",
    "external-systems": "External Systems",
    integrations: "External Systems",
  };

  // Check if the entire segment is a special case
  const lowerSegment = segment.toLowerCase();
  if (specialCases[lowerSegment]) {
    return specialCases[lowerSegment];
  }

  // Split on hyphens and convert each word to title case
  return segment
    .split("-")
    .map((word) => {
      const lowerWord = word.toLowerCase();

      // Check if this individual word is a special case
      if (specialCases[lowerWord]) {
        return specialCases[lowerWord];
      }

      // Standard title case conversion
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Remove null, undefined, and empty string values from an object
 * Useful for cleaning up API request payloads
 *
 * @param obj - Object to clean
 * @returns New object with empty values removed
 *
 * @example
 * ```typescript
 * omitEmpty({ name: "John", email: "", age: null, city: "Seattle" });
 * // Returns: { name: "John", city: "Seattle" }
 * ```
 */
export function omitEmpty<T extends Record<string, unknown>>(
  obj: T,
): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => {
      // Keep values that are not null, undefined, or empty string
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    }),
  ) as Partial<T>;
}
